import React, { useMemo, useState } from 'react'
import CreatorFrame from '../../../shared/CreatorFrame'
import { useScrollLock } from '../../../../_utils/useScrollLock'
import { hapticTick } from '../../../../_utils/haptics'
import { useSheetDrag } from '../social/components/studio/useSheetDrag'
import { BrandPanel } from '../email-composer/components/BrandPanel'
import { ImagePanel } from '../email-composer/components/ImagePanel'
import { SmoothEmailFrame } from './SmoothEmailFrame'
import { resolveTheme } from '../email-composer/email/theme'
import { ApplyBrandToLocations } from './ApplyBrandToLocations'
import { previewHtml } from './brandPreview'
import '../social/Social.css'
import './BrandStudio.css'

// Your brand on a phone: the same studio the social posts use. The email
// fills the screen, the tools run along the bottom, and a tool opens as a
// sheet under it, so every change shows as it's made.

const I = ({ children }) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
const TOOLS = [
  { id: 'look', label: 'Look', title: 'Look', icon: <I><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M4 10h16M10 10v10" /></I> },
  { id: 'colour', label: 'Colour', title: 'Brand colour', icon: <I><path d="M12 3a9 9 0 1 0 0 18c1.4 0 2-1 1.5-2-.6-1.2.2-2.5 1.6-2.5H17a4 4 0 0 0 4-4C21 6.6 17 3 12 3z" /><circle cx="7.5" cy="11" r="1" /><circle cx="10" cy="7" r="1" /><circle cx="15" cy="7.5" r="1" /></I> },
  { id: 'logo', label: 'Logo', title: 'Logo', icon: <I><rect x="3.5" y="5" width="17" height="14" rx="3" /><circle cx="9" cy="10" r="1.6" /><path d="M4 17l5-4 4 3 3-2 4 3" /></I> },
  { id: 'fonts', label: 'Fonts', title: 'Fonts', icon: <I><path d="M5 18l5-13 5 13M7 13.5h6M17 10h3m-1.5 0V18" /></I> },
  { id: 'buttons', label: 'Buttons', title: 'Buttons', icon: <I><rect x="3.5" y="8" width="17" height="8" rx="4" /><path d="M9 12h6" /></I> },
  { id: 'corners', label: 'Corners', title: 'Corners', icon: <I><path d="M5 20V12a7 7 0 0 1 7-7h8" /></I> },
]

export const BrandStudio = ({ brand, onDone, leaving }) => {
  useScrollLock(true)
  const [tool, setTool] = useState(null)
  const [tall, setTall] = useState(false)
  const [pickingLogo, setPickingLogo] = useState(false)
  const theme = resolveTheme(brand.spec)
  const html = useMemo(() => previewHtml(brand.spec), [brand.spec])
  const def = TOOLS.find(t => t.id === tool)
  const close = () => { setTool(null); setTall(false); setPickingLogo(false) }
  const drag = useSheetDrag({ tall, setTall, close })

  return (
    <CreatorFrame
      name="Your brand"
      onClose={onDone}
      headerExtra={<span className={`bs-status${brand.saved ? ' is-saved' : ''}`} role="status" aria-live="polite">{brand.saved ? 'Saved' : 'Saves as you go'}</span>}
      primaryAction={{ label: leaving ? 'Saving…' : 'Done', onClick: onDone, disabled: leaving }}
    >
      <div className={`sx-studio bs-studio is-phone${tool ? ' has-panel' : ''}${tall ? ' is-tall' : ''}`}>
        <nav className="sx-rail" aria-label="Brand tools">
          {TOOLS.map(t => (
            <button key={t.id} type="button" className={`sx-rail-btn${tool === t.id ? ' is-on' : ''}`} aria-pressed={tool === t.id}
              onClick={() => { hapticTick(); setPickingLogo(false); setTall(false); setTool(tool === t.id ? null : t.id) }}>
              <span className="sx-rail-icon">{t.icon}</span>
              <span className="sx-rail-label">{t.label}</span>
            </button>
          ))}
        </nav>

        {tool && (
          <aside className="sx-panel" aria-label={def.title}>
            <header className="sx-panel-head" {...drag}>
              <button type="button" className="sx-grabber" onClick={() => setTall(v => !v)} aria-label={tall ? 'Make the panel smaller' : 'Make the panel bigger'}><span /></button>
              <h2 className="sx-panel-title">{pickingLogo ? 'Choose your logo' : def.title}</h2>
              <button type="button" className="sx-icon-btn sx-panel-close" onClick={close} aria-label="Close panel">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" /></svg>
              </button>
            </header>
            <div className="sx-panel-body" key={tool}>
              {pickingLogo ? (
                <ImagePanel salon={brand.salon} onSelect={(url) => { brand.update({ ...brand.spec, logo: url, logoShape: undefined, logoShapeFor: undefined }); setPickingLogo(false) }} />
              ) : (
                <BrandPanel spec={brand.spec} salonKit={brand.salonKit} onChange={brand.update} onPickLogo={() => setPickingLogo(true)} kitSaved={brand.saved} only={tool} />
              )}
            </div>
          </aside>
        )}

        <div className="sx-stage bs-stage" onClick={() => { if (tool) { hapticTick(); close() } }}>
          <div className="bs-mail" style={{ background: theme.palette.canvas }}>
            {brand.loading ? <div className="brp-hold" /> : <SmoothEmailFrame html={html} width={600} title="Your brand in an email" />}
          </div>
          {brand.syncNote && <p className="bs-note">{brand.syncNote}</p>}
          <ApplyBrandToLocations changes={brand.changes} flush={brand.flush} />
        </div>
      </div>
    </CreatorFrame>
  )
}

export default BrandStudio
